import React, { FC } from 'react';
import { formatClassName } from '../../utils';
import { DataAttributes } from '../../utils/pickDataAttributes';
import { kebabCase } from 'lodash';
import { ChipProps } from './types';
import * as Core from '../../core';
import { tokens } from '@everfi/design-system';

export interface DataObject {
  action?: string;
  objectId?: string | number;
  objectType?: string;
  componentType?: string;
}

export const Chip: FC<ChipProps> = ({
  canClose = true,
  category,
  data = {},
  disabled,
  onClick,
  onRemove = () => {},
  sortable,
  value,
  ...props
}) => {
  // @ts-ignore
  const getChipClasses = (): string | undefined => formatClassName(['chip', { disabled }]);

  const getDataAttributes = (data: DataObject): object => {
    const dataProps: DataAttributes = {};
    if (data.action) {
      dataProps['data-action'] = data.action;
    }
    if (data.objectId) {
      dataProps['data-object-id'] = data.objectId;
    }
    if (data.objectType) {
      dataProps['data-object-type'] = data.objectType;
    }
    if (data.componentType) {
      dataProps['data-component-type'] = data.componentType;
    } else {
      dataProps['data-component-type'] = value ? `${kebabCase(value)}-chip` : 'chip';
    }
    return dataProps;
  };

  const performOnMouseDown = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const performOnClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && onClick) {
      onClick();
    }
  };

  const performOnRemove = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      onRemove();
    }
  };

  const dataAttributes = getDataAttributes(data);
  return (
    <Core.Layout
      as="button"
      className={getChipClasses()}
      onMouseDown={performOnMouseDown}
      onClick={performOnClick}
      borderRadius={tokens.BorderRadiiSm}
      {...props}
      {...dataAttributes}
    >
      <Core.Tag
        colorScheme={disabled ? 'gray' : 'brand'}
        size={Core.TagSize.MEDIUM}
        isClosable={canClose}
        closeClick={performOnRemove}
      >
        {sortable && <Core.Icon iconName="grip-lines" prefix="far" />}
        {category && (
          <Core.Layout as="span" fontWeight="bold" whiteSpace="nowrap">
            {category}
            {value && ': '}
          </Core.Layout>
        )}
        {value}
      </Core.Tag>
    </Core.Layout>
  );
};

Chip.displayName = 'Chip';
